import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, ArrowLeft, Inbox, IndianRupee, Newspaper, CreditCard, ShoppingBag, ShieldCheck, Timer, ImageIcon, Megaphone, Landmark, LifeBuoy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { LeadsTab } from "@/components/admin/LeadsTab";
import { PricesTab } from "@/components/admin/PricesTab";
import { ArticlesTab } from "@/components/admin/ArticlesTab";
import { PaymentsTab } from "@/components/admin/PaymentsTab";
import { OrdersTab } from "@/components/admin/OrdersTab";
import { KycTab } from "@/components/admin/KycTab";
import { DealsTab } from "@/components/admin/DealsTab";
import { LogosTab } from "@/components/admin/LogosTab";
import { BannerTab } from "@/components/admin/BannerTab";
import { BankDetailsTab } from "@/components/admin/BankDetailsTab";
import { SupportTab } from "@/components/admin/SupportTab";

const inputCls =
  "w-full border border-slate-200 bg-[#FFFFFF]/60 px-4 py-3.5 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors duration-300 focus:border-[#6D28D9]/60";

const TABS = [
  { id: "leads", label: "Enquiries", icon: Inbox },
  { id: "orders", label: "Share Orders", icon: ShoppingBag },
  { id: "bank", label: "Bank Details", icon: Landmark },
  { id: "support", label: "Support", icon: LifeBuoy },
  { id: "kyc", label: "KYC", icon: ShieldCheck },
  { id: "deals", label: "Deals", icon: Timer },
  { id: "logos", label: "Logos", icon: ImageIcon },
  { id: "banner", label: "Banner", icon: Megaphone },
  { id: "prices", label: "Prices & Alerts", icon: IndianRupee },
  { id: "articles", label: "Insight Notes", icon: Newspaper },
  { id: "payments", label: "Payments", icon: CreditCard },
];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => setUser(res.data.role === "admin" ? res.data : false))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await api.post("/auth/login", { email, password });
      if (res.data.role !== "admin") {
        setError("This area is for the admin desk. Investor accounts can sign in from the main site.");
        return;
      }
      setUser(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#FFFFFF] text-slate-900">
      <header className="border-b border-slate-200 bg-[#FFFFFF]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
          <Link to="/" data-testid="admin-back-home" className="flex items-center gap-3">
            <img src="/logo.png" alt="PRE-IPO EXPERT" className="h-10 w-10 rounded-full object-contain" />
            <span className="font-display text-lg font-semibold tracking-[0.18em]">
              PRE-IPO <span className="text-[#6D28D9]">EXPERT</span>
            </span>
          </Link>
          {user && (
            <div className="flex items-center gap-3">
              <Link data-testid="admin-investor-panel-link" to="/client/dashboard" className="hidden items-center gap-2 border border-slate-200 px-4 py-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:border-[#6D28D9]/40 hover:text-[#6D28D9] sm:flex">
                Investor Panel
              </Link>
              <button
                data-testid="admin-logout-button"
                onClick={logout}
                className="flex items-center gap-2 border border-slate-200 px-4 py-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:border-rose-600/40 hover:text-rose-700"
              >
                <LogOut className="h-3.5 w-3.5" /> Logout
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        {checking ? (
          <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-slate-500">Checking session…</p>
        ) : !user ? (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mx-auto max-w-md">
            <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-[#6D28D9]/90">Restricted</p>
            <h1 className="font-display mt-4 text-3xl font-semibold tracking-tight">Desk Login</h1>
            <p className="mt-3 text-sm text-slate-500">Admin access to enquiries, prices and insight notes.</p>
            <form data-testid="admin-login-form" onSubmit={login} className="mt-8 space-y-4 border border-slate-200 bg-[#F6F4FD]/70 p-8">
              <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email" className={inputCls} />
              <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} />
              {error && <p data-testid="admin-login-error" className="text-xs text-rose-600">{error}</p>}
              <button data-testid="admin-login-submit-button" type="submit" className="w-full bg-[#6D28D9] px-8 py-4 font-mono2 text-xs font-semibold uppercase tracking-[0.22em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6]">
                Sign In
              </button>
            </form>
            <Link to="/" className="mt-6 flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#6D28D9]">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to site
            </Link>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-[#6D28D9]/90">Admin Desk</p>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
              <h1 className="font-display text-3xl font-semibold tracking-tight">Desk Console</h1>
              <p className="text-sm text-slate-500">Signed in as {user.email}</p>
            </div>

            <nav data-testid="admin-tabs" className="mt-10 flex gap-px border border-slate-200 bg-slate-200">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  data-testid={`admin-tab-${id}`}
                  onClick={() => setTab(id)}
                  className={`flex flex-1 items-center justify-center gap-2 px-4 py-3.5 font-mono2 text-[11px] uppercase tracking-[0.2em] transition-colors sm:flex-none sm:px-6 ${tab === id ? "bg-[#6D28D9] text-[#FFFFFF]" : "bg-[#F6F4FD] text-slate-500 hover:text-[#6D28D9]"}`}
                >
                  <Icon className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </nav>

            <div className="mt-10">
              {tab === "leads" && <LeadsTab />}
              {tab === "orders" && <OrdersTab />}
              {tab === "bank" && <BankDetailsTab />}
              {tab === "support" && <SupportTab />}
              {tab === "kyc" && <KycTab />}
              {tab === "deals" && <DealsTab />}
              {tab === "logos" && <LogosTab />}
              {tab === "banner" && <BannerTab />}
              {tab === "prices" && <PricesTab />}
              {tab === "articles" && <ArticlesTab />}
              {tab === "payments" && <PaymentsTab />}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
